import { useEffect, useState } from 'react';
import { Panel, PanelHeader, PanelBody } from '../ui/Panel';
import { EmptyState, ErrorNotice, SkeletonRows } from '../ui/States';
import { dayLabel, torontoToday } from '../../lib/events';
import { errorMessage } from '../../lib/programs';
import { AgendaSession, FORMAT_LABEL, ROLE_LABEL, printedName, timeRange, useAgenda, useProgram } from './agendaTypes';

const torontoClock = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date());

function stateOf(s: AgendaSession, next: AgendaSession | undefined, isToday: boolean, now: string) {
  if (!isToday) return 'later';
  const end = s.endTime || next?.startTime || '23:59';
  if (now >= end) return 'done';
  if (now >= s.startTime) return 'now';
  return 'later';
}

/** The day's sessions in order, following the Toronto clock on the day itself. */
export default function DayRunOfShow({ eventId, day: fixed }: { eventId: string; canEdit?: boolean; day?: string }) {
  const agenda = useAgenda(eventId);
  const program = useProgram(eventId);
  const [now, setNow] = useState(torontoClock());
  useEffect(() => {
    const t = setInterval(() => setNow(torontoClock()), 30000);
    return () => clearInterval(t);
  }, []);
  const today = torontoToday();
  const days = agenda.data?.event.days.map((d) => d.date) ?? [];
  const [day, setDay] = useState<string | null>(null);
  const current = fixed ?? (day && days.includes(day) ? day : days.includes(today) ? today : days[0]);
  const list = (agenda.data?.sessions ?? []).filter((s) => s.day === current);
  const isToday = current === today;
  const nextIndex = isToday ? list.findIndex((s) => s.startTime > now) : -1;

  return (
    <Panel>
      <PanelHeader title="Run of show" note={isToday ? `Now ${now}` : fixed ? dayLabel(fixed) : undefined}>
        <div className="flex gap-1">
          {(fixed ? [] : days).map((d) => (
            <button
              key={d}
              onClick={() => setDay(d)}
              className={`px-2 py-1 text-small ${d === current ? 'border-b border-line-strong font-semibold' : 'text-ink-2'}`}
            >
              {dayLabel(d)}
            </button>
          ))}
        </div>
      </PanelHeader>
      <PanelBody>
        {agenda.isLoading ? <SkeletonRows rows={5} /> : null}
        {agenda.isError ? <ErrorNotice>{errorMessage(agenda.error, 'Could not load the agenda')}</ErrorNotice> : null}
        {agenda.data && !list.length ? (
          <EmptyState title="Nothing scheduled">No sessions on this day yet.</EmptyState>
        ) : null}
        <ol>
          {list.map((s, i) => {
            const state = stateOf(s, list[i + 1], isToday, now);
            const people = s.people.filter((p) => p.confirmation !== 'declined');
            return (
              <li
                key={s.id}
                className={`flex gap-4 border-b border-line py-3 px-2 ${state === 'now' ? 'bg-primary-tint' : ''} ${state === 'done' ? 'text-ink-2' : ''} ${i === nextIndex && state !== 'now' ? 'border-l-4 border-l-line-strong' : ''}`}
              >
                <div className="w-[140px] shrink-0">
                  <div className="text-table font-semibold">{timeRange(s)}</div>
                  <div className="text-small">
                    {state === 'now' ? 'Now' : i === nextIndex ? 'Next' : state === 'done' ? 'Done' : ''}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="text-table font-semibold">{s.title}</div>
                  <div className="text-small">
                    {[
                      s.room,
                      FORMAT_LABEL[s.format],
                      s.interpretedEffective ? 'interpretation' : null,
                      s.hybrid ? 'online too' : null,
                      s.recordingPlanned ? 'recorded' : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                  {(program.data?.dignitaries ?? [])
                    .filter((g) => g.sessionId === s.id && g.reply !== 'declined')
                    .map((g) => (
                      <div key={g.id} className="text-small mt-1">
                        <span className="text-ink-2">{g.reply === 'video' ? 'Video greeting' : 'Greeting'}:</span>{' '}
                        {g.name}
                        {g.office ? `, ${g.office}` : ''} ({g.minutes ?? 5} min)
                        {g.reply === 'video' && !g.videoReceivedAt ? (
                          <span className="text-warning"> — video not received</span>
                        ) : null}
                      </div>
                    ))}
                  {(program.data?.competitions ?? [])
                    .filter((c) => c.sessionId === s.id)
                    .map((c) => (
                      <div key={c.id} className="text-small mt-1">
                        <span className="text-ink-2">{c.name}:</span>{' '}
                        {c.competitors
                          .filter((x) => ['finalist', 'winner'].includes(x.status))
                          .sort((a, b) => (a.presentationOrder ?? 99) - (b.presentationOrder ?? 99))
                          .map((x) => x.name)
                          .join(', ') || 'no finalists yet'}
                        {c.timeCapMinutes ? ` · ${c.timeCapMinutes} min each` : ''}
                      </div>
                    ))}
                  {people.some((p) => p.speakerType === 'elder') ? (
                    <div className="text-small mt-1 font-semibold">
                      Elder:{' '}
                      {people
                        .filter((p) => p.speakerType === 'elder')
                        .map(printedName)
                        .join('; ')}
                    </div>
                  ) : null}
                  {people.length ? (
                    <div className="text-small mt-1">
                      {(['emcee', 'moderator', 'speaker', 'panelist'] as const).map((role) => {
                        const names = people.filter((p) => p.role === role && p.speakerType !== 'elder');
                        return names.length ? (
                          <div key={role}>
                            <span className="text-ink-2">{ROLE_LABEL[role]}:</span>{' '}
                            {names
                              .map((p) => `${printedName(p)}${p.format === 'virtual' ? ' (online)' : ''}`)
                              .join('; ')}
                          </div>
                        ) : null;
                      })}
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      </PanelBody>
    </Panel>
  );
}
